import { Box, Flex, Text, UnstyledButton } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import classes from './PersonResultRow.module.css';

type Props = {
  title: string;
  subtitle?: string;
  actionLabel: string;
  /** Distinguishes this row's action when a long list would otherwise repeat one label. */
  actionAriaLabel?: string;
  onAction: () => void;
  /**
   * Spreads the action's hit area over the whole row, for a list where picking the person
   * is the row's only action. The button stays the one focusable element, which a second
   * row-level button wrapping it could not be.
   */
  stretchAction?: boolean;
};

const actionStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
};

/** A person offered for selection: who they are, and the action that picks them. */
export function PersonResultRow({
  title,
  subtitle,
  actionLabel,
  actionAriaLabel,
  onAction,
  stretchAction = false,
}: Props) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap="sm"
      className={stretchAction ? `${classes.row} ${classes.stretched}` : classes.row}
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black" truncate>
          {title}
        </Text>
        {subtitle && (
          <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.7" mt="xxxs" truncate>
            {subtitle}
          </Text>
        )}
      </Box>
      <UnstyledButton
        style={actionStyle}
        className={stretchAction ? classes.stretchedAction : undefined}
        aria-label={actionAriaLabel}
        onClick={onAction}
      >
        <Flex align="center" gap={2}>
          {actionLabel}
          <IconPlus size={16} stroke={2.5} color="var(--mantine-color-brand-6)" />
        </Flex>
      </UnstyledButton>
    </Flex>
  );
}
